import {
  FundraisingModel,
  FundraisingTicketingInfo,
  SendTicket,
  Step,
  TABS,
} from 'src/models/FundraisingModel';
import { FundraisingProps, renderHasChanged } from 'src/components/Pages/AddFundraisingPage';
import Button from '@material-ui/core/Button';
import { MouseEvent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Store, useAppDispatch } from 'src/redux/Store';
import { FORM } from 'src/constants/Form';
import { ErrorBag } from 'src/models/ErrorModel';
import { setError } from 'src/redux/actions/errorsActions';
import { setAlert } from 'src/redux/actions/alertActions';
import { ALERT_TYPE } from 'src/constants/AlertType';
import FundraisingTicketingValidator from 'src/validations/FundraisingTicketingValidator';
import FormCheckbox from 'src/components/Control/FormControls/FormCheckbox';
import Subtitle from 'src/components/UI/Subtitle';
import BottomRow from 'src/components/UI/BottomRow';
import FundraiserTitle from 'src/components/UI/FundRaiserTitle';
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@material-ui/core';
import FormTextField from 'src/components/Control/FormControls/FormTextField';
import Row from 'src/components/UI/Row';
import FormDateTimePicker from 'src/components/Control/FormControls/FormDateTimePicker';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import moment from 'moment-timezone-all';
import Scroller from 'src/components/UI/Scroller';

const FundraisingTicketingForm = (props: FundraisingProps) => {
  const {
    onChange,
    setErrors,
    fundraisingState,
    setHasChanged,
    hasChanged,
  } = props;
  const dispatch = useAppDispatch();
  const lang = useSelector((state: Store) => state.language.language);

  const getInitialTicketingState = (): FundraisingTicketingInfo => {
    if (fundraisingState?.ticketingInfo) {
      return { ...fundraisingState.ticketingInfo };
    }
    return {
      ticket_send_mode: SendTicket.all_in_one,
      event_date: moment().startOf('day'),
    } as FundraisingTicketingInfo;
  };

  const [ticketingState, setTicketingState] = useState<FundraisingTicketingInfo>(
    getInitialTicketingState(),
  );

  const [openTicketing, setOpenTicketing] = useState(
    !!fundraisingState?.ticketingInfo?.title
    || !!fundraisingState?.ticketingInfo?.location_name,
  );

  useEffect(() => {
    if (!fundraisingState.ticketingInfo) return;
    setTicketingState({ ...fundraisingState.ticketingInfo });
  }, [fundraisingState]);

  useEffect(() => {
    dispatch(setError({ [FORM.fundraisingTickets]: {} }));
  }, []);

  const onTicketValueChange = (value: any, field: string) => {
    setHasChanged(true);
    setTicketingState({
      ...ticketingState,
      [field]: value,
    });
  };

  const handleSubmit = (event: MouseEvent<HTMLElement>, advance: boolean) => {
    event.preventDefault();
    setErrors(TABS.ticketing, false);
    dispatch(setError({ [FORM.fundraisingTickets]: {} }));

    if (openTicketing) {
      const errorBag = {} as ErrorBag;
      try {
        FundraisingTicketingValidator(lang)
          .validateSync(ticketingState, { abortEarly: false });
      } catch (validationErrors: any) {
        dispatch(setAlert({
          type: ALERT_TYPE.error,
          code: 'forms.fundraising.ticketing_validation_errors',
        }));
        validationErrors.inner.forEach((e: any) => {
          if (!errorBag[e.path]) {
            errorBag[e.path] = [];
          }
          errorBag[e.path].push(e.message);
        });
        dispatch(setError({ [FORM.fundraisingTickets]: errorBag }));
        setErrors(TABS.ticketing, true);
        return;
      }
    }

    onChange(
      {
        ...fundraisingState,
        ticketingInfo: openTicketing ? ticketingState : null,
      } as FundraisingModel,
      Step.ticketing,
      advance,
    );
  };

  return (
    <>
      <FundraiserTitle>Ticketing</FundraiserTitle>
      <Scroller height="565px">
        <Subtitle>Ticketing</Subtitle>
        <Row>
          <FormCheckbox
            checked={openTicketing}
            label="Ticketing Information"
            name="ticketing_information"
            onChange={(value: boolean) => setOpenTicketing(value)} />
        </Row>
        {openTicketing && (
          <>
            <Row>
              <FormControl>
                <RadioGroup
                  row
                  aria-labelledby="ticket_send_mode"
                  name="ticket_send_mode"
                  value={ticketingState.ticket_send_mode}
                  onChange={(e: any, value: string) => onTicketValueChange(value, 'ticket_send_mode')}>
                  <FormControlLabel
                    value={SendTicket.none}
                    control={<Radio />}
                    label="Do not send ticket" />
                  <FormControlLabel
                    value={SendTicket.all_in_one}
                    control={<Radio />}
                    label="Send all ticket in one PDF" />
                  <FormControlLabel
                    value={SendTicket.individual}
                    control={<Radio />}
                    label="Send each ticket in a separate PDF" />
                </RadioGroup>
              </FormControl>
            </Row>
            <Row>
              <div style={{ width: '75%' }}>
                <FormTextField
                  form={FORM.fundraisingTickets}
                  label="Name of the event"
                  name="title"
                  onChange={onTicketValueChange}
                  value={ticketingState.title} />
              </div>
              <div style={{ width: '25%' }}>
                <FormDateTimePicker
                  form={FORM.fundraisingTickets}
                  label="Event date"
                  name="event_date"
                  onChange={
                    (value: MaterialUiPickersDate) => onTicketValueChange(value, 'event_date')
                  }
                  value={ticketingState.event_date || moment()
                    .startOf('day')}
                  showTime />
              </div>
            </Row>
            <Row>
              <div style={{ width: '75%' }}>
                <FormTextField
                  form={FORM.fundraisingTickets}
                  label="Location Name"
                  name="location_name"
                  onChange={onTicketValueChange}
                  value={ticketingState.location_name} />
              </div>
            </Row>
            <Row>
              <div style={{ width: '75%' }}>
                <FormTextField
                  form={FORM.fundraisingTickets}
                  label="Location Address"
                  name="location_address"
                  onChange={onTicketValueChange}
                  value={ticketingState.location_address} />
              </div>
            </Row>
            <Row>
              <div style={{ width: '75%' }}>
                <FormTextField
                  form={FORM.fundraisingTickets}
                  label="Contact name"
                  name="contact_name"
                  onChange={onTicketValueChange}
                  value={ticketingState.contact_name} />
              </div>
            </Row>
            <Row>
              <div style={{ width: '50%' }}>
                <FormTextField
                  form={FORM.fundraisingTickets}
                  label="Contact phone"
                  name="contact_phone"
                  onChange={onTicketValueChange}
                  value={ticketingState.contact_phone} />
              </div>
              <div style={{ width: '50%' }}>
                <FormTextField
                  form={FORM.fundraisingTickets}
                  label="Contact email"
                  name="contact_email"
                  onChange={onTicketValueChange}
                  value={ticketingState.contact_email} />
              </div>
            </Row>
            <Row>
              <div style={{ width: '100%' }}>
                <FormTextField
                  form={FORM.fundraisingTickets}
                  label="Ticket message"
                  name="ticket_message"
                  onChange={onTicketValueChange}
                  value={ticketingState.ticket_message} />
              </div>
            </Row>
          </>
        )}
        <BottomRow>
          <Button
            variant="contained"
            color="primary"
            onClick={(e) => handleSubmit(e, false)}
            key="submit-button">
            Save{renderHasChanged(!hasChanged)}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={(e) => handleSubmit(e, true)}
            key="submit-button-next">
            Save{renderHasChanged(!hasChanged)} and Next Step
          </Button>
        </BottomRow>
      </Scroller>
    </>
  );
};

export default FundraisingTicketingForm;
